import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCoreModelContext } from '../context/core-model-context.js';
import {
	applyGridStateToDrawingManager,
	createDefaultGridAppearance,
	createDefaultGridState,
	getDefaultGridTypeForCadTool,
	useGridBridge,
} from './bridges/grid-bridge.js';
import { useInteractionStateBridge } from './bridges/interaction-state-bridge.js';
import { deserializeGraphicRenderState, useLayerBridge } from './bridges/layer-bridge.js';
import { useViewHydrationBridge } from './bridges/view-hydration-bridge.js';
import { CadRendererSnapshotEvent, CadRendererSyncNamespace } from './broadcast-manager.js';
import { followGridAppearanceStorage, useGridAppearanceStore } from './grid-appearance-store.js';
import { useCadRendererSyncCore } from './sync-core.js';
import { compareVersion } from './sync-protocol.js';

import type { ModelGrid, ModelType } from '@cadstrom/sigma-engine-shared';
import type {
	CadRendererGridAppearance,
	CadRendererGridSettings,
	CadRendererGridState,
	GridSnapshotPayload,
} from './bridges/grid-bridge.js';
import type {
	CadRendererLayerState,
	LayerSnapshotPayload,
	SerializableGraphicRenderState,
} from './bridges/layer-bridge.js';
import type {
	CadRendererSyncSessionValue,
	DrawingManagerRegistration,
	UseCadRendererSyncSessionOptions,
} from './sync-types.js';

/**
 * Creates the sync session value for one CAD renderer tree.
 *
 * The session owns the broadcast manager lifecycle, tracks local layer/grid snapshots, registers
 * drawing managers that need synchronized state applied, and installs bridge hooks for interaction,
 * layer, grid, and initial view hydration messages.
 *
 * Grid appearance is one setting per model type rather than per design. Without `defaultGridType`
 * it is the user's saved preference, shared with every window, and starts from the authoring tool's
 * own grid style; with it, it starts from that style and lives only in this session.
 */
export function useCadRendererSyncSession({
	syncScope,
	interactionManager,
	activeView,
	setActiveView,
	getActiveViewport,
	restoreViewport,
	suppressHistoryForHydration,
	defaultGridType,
	broadcastOptions,
}: UseCadRendererSyncSessionOptions): CadRendererSyncSessionValue {
	const core = useCadRendererSyncCore({ syncScope, broadcastOptions });
	const cadTool = useCoreModelContext(false)?.coreModel.type;
	const fallbackGridType = defaultGridType ?? getDefaultGridTypeForCadTool(cadTool);
	const defaultGridAppearance = useMemo(() => createDefaultGridAppearance(fallbackGridType), [fallbackGridType]);
	const [layerStates, setLayerStates] = useState<Record<string, CadRendererLayerState>>({});
	const [gridStates, setGridStates] = useState<Record<string, CadRendererGridState>>({});
	const savedGridAppearances = useGridAppearanceStore((state) => state.appearances);
	const setSavedGridAppearance = useGridAppearanceStore((state) => state.setAppearance);
	const [sessionGridAppearances, setSessionGridAppearances] = useState<
		Partial<Record<ModelType, CadRendererGridAppearance>>
	>({});
	const isGridAppearanceSaved = defaultGridType === undefined;
	const gridAppearances = isGridAppearanceSaved ? savedGridAppearances : sessionGridAppearances;
	const gridAppearancesRef = useRef(gridAppearances);
	const layerStatesRef = useRef<Record<string, CadRendererLayerState>>({});
	const gridStatesRef = useRef<Record<string, CadRendererGridState>>({});
	const registrationsRef = useRef(new Map<number, DrawingManagerRegistration>());
	const nextRegistrationIdRef = useRef(1);
	const activeViewRef = useRef(activeView);
	const getActiveViewportRef = useRef(getActiveViewport);
	const restoreViewportRef = useRef(restoreViewport);
	const setActiveViewRef = useRef(setActiveView);
	const suppressHistoryForHydrationRef = useRef(suppressHistoryForHydration);
	const viewUpdatedAtRef = useRef(0);

	useEffect(() => {
		activeViewRef.current = activeView;
		viewUpdatedAtRef.current = Date.now();
	}, [activeView]);

	useEffect(() => {
		layerStatesRef.current = layerStates;
	}, [layerStates]);

	useEffect(() => {
		gridStatesRef.current = gridStates;
	}, [gridStates]);

	useEffect(() => {
		if (isGridAppearanceSaved) {
			return followGridAppearanceStorage();
		}
	}, [isGridAppearanceSaved]);

	useEffect(() => {
		getActiveViewportRef.current = getActiveViewport;
		restoreViewportRef.current = restoreViewport;
		setActiveViewRef.current = setActiveView;
		suppressHistoryForHydrationRef.current = suppressHistoryForHydration;
	}, [getActiveViewport, restoreViewport, setActiveView, suppressHistoryForHydration]);

	const applyLayerState = useCallback((state: CadRendererLayerState) => {
		for (const registration of registrationsRef.current.values()) {
			if (registration.layerSet !== state.layerSet || registration.drawingManager.isDestroyed) {
				continue;
			}
			registration.drawingManager.setVisibleLayers([...state.visibleLayerIds]);
			for (const [layerId, color] of Object.entries(state.colorOverrides)) {
				registration.drawingManager.colorProvider.setLayerColor(Number(layerId), deserializeGraphicRenderState(color));
			}
		}
	}, []);

	const getGridAppearance = useCallback(
		(modelType: ModelType): CadRendererGridAppearance => gridAppearancesRef.current[modelType] ?? defaultGridAppearance,
		[defaultGridAppearance],
	);

	const getRegistrationGridSettings = useCallback(
		(registration: DrawingManagerRegistration): CadRendererGridSettings => {
			const state =
				gridStatesRef.current[registration.designId] ??
				createDefaultGridState(registration.designId, registration.modelGrid, core.windowId, 0);
			return {
				...getGridAppearance(registration.modelType),
				distance: state.distance,
				units: state.units,
			};
		},
		[core.windowId, getGridAppearance],
	);

	const applyGrid = useCallback(
		(shouldApply: (registration: DrawingManagerRegistration) => boolean) => {
			for (const registration of registrationsRef.current.values()) {
				if (registration.drawingManager.isDestroyed || !shouldApply(registration)) {
					continue;
				}
				applyGridStateToDrawingManager(registration, getRegistrationGridSettings(registration));
			}
		},
		[getRegistrationGridSettings],
	);

	// Appearance changes reach this session as new store state, whether they come from this window,
	// another window, or a fresh hydration, so the renderers are redrawn from here.
	useEffect(() => {
		gridAppearancesRef.current = gridAppearances;
		applyGrid(() => true);
	}, [applyGrid, gridAppearances]);

	const updateLayerState = useCallback(
		(next: CadRendererLayerState, publish: boolean) => {
			const previous = layerStatesRef.current[next.layerSet];
			if (previous && compareVersion(next, previous) <= 0) {
				return;
			}

			const updated = { ...layerStatesRef.current, [next.layerSet]: next };
			layerStatesRef.current = updated;
			setLayerStates(updated);
			applyLayerState(next);
			if (publish) {
				core.publish(CadRendererSyncNamespace.Layers, CadRendererSnapshotEvent.Snapshot, {
					layerStates: [next],
				} satisfies LayerSnapshotPayload);
			}
		},
		[applyLayerState, core],
	);

	const updateGridState = useCallback(
		(next: CadRendererGridState, publish: boolean) => {
			const previous = gridStatesRef.current[next.designId];
			if (previous && compareVersion(next, previous) <= 0) {
				return;
			}

			const updated = { ...gridStatesRef.current, [next.designId]: next };
			gridStatesRef.current = updated;
			setGridStates(updated);
			applyGrid((registration) => registration.designId === next.designId);
			if (publish) {
				core.publish(CadRendererSyncNamespace.Grid, CadRendererSnapshotEvent.Snapshot, {
					gridStates: [next],
				} satisfies GridSnapshotPayload);
			}
		},
		[applyGrid, core],
	);

	const registerDrawingManager = useCallback(
		(registration: DrawingManagerRegistration) => {
			const id = nextRegistrationIdRef.current++;
			registrationsRef.current.set(id, registration);

			const layerState = layerStatesRef.current[registration.layerSet];
			if (layerState) {
				applyLayerState(layerState);
			}
			applyGridStateToDrawingManager(registration, getRegistrationGridSettings(registration));

			return () => {
				registrationsRef.current.delete(id);
			};
		},
		[applyLayerState, getRegistrationGridSettings],
	);

	const getDefaultVisibleLayerIds = useCallback((layerSet: string): number[] => {
		const registration = Array.from(registrationsRef.current.values()).find((entry) => entry.layerSet === layerSet);
		if (!registration) {
			return [];
		}
		return Array.from(registration.drawingManager.getLayersInUse())
			.filter((layer) => registration.drawingManager.getLayerIsVisible(layer.id))
			.map((layer) => layer.id);
	}, []);

	const setLayerVisibility = useCallback(
		(layerSet: string, visibleLayerIds: readonly number[]) => {
			const previous = layerStatesRef.current[layerSet];
			const next: CadRendererLayerState = {
				layerSet,
				visibleLayerIds: [...visibleLayerIds],
				colorOverrides: previous?.colorOverrides ?? {},
				updatedAt: Date.now(),
				sourceWindowId: core.windowId,
			};
			updateLayerState(next, true);
		},
		[core.windowId, updateLayerState],
	);

	const setLayerColorOverride = useCallback(
		(layerSet: string, layerId: number, color: SerializableGraphicRenderState) => {
			const previous = layerStatesRef.current[layerSet];
			const next: CadRendererLayerState = {
				layerSet,
				visibleLayerIds: previous?.visibleLayerIds ?? getDefaultVisibleLayerIds(layerSet),
				colorOverrides: {
					...(previous?.colorOverrides ?? {}),
					[layerId]: color,
				},
				updatedAt: Date.now(),
				sourceWindowId: core.windowId,
			};
			updateLayerState(next, true);
		},
		[core.windowId, getDefaultVisibleLayerIds, updateLayerState],
	);

	const getGridSettings = useCallback(
		(designId: string, modelType: ModelType, modelGrid: ModelGrid): CadRendererGridSettings => {
			const state = gridStates[designId] ?? createDefaultGridState(designId, modelGrid, core.windowId, 0);
			return {
				...(gridAppearances[modelType] ?? defaultGridAppearance),
				distance: state.distance,
				units: state.units,
			};
		},
		[core.windowId, defaultGridAppearance, gridAppearances, gridStates],
	);

	const setGridSettings = useCallback(
		(designId: string, modelType: ModelType, modelGrid: ModelGrid, settings: Partial<CadRendererGridSettings>) => {
			const { distance, units, ...appearance } = settings;
			if (distance !== undefined || units !== undefined) {
				const previous =
					gridStatesRef.current[designId] ?? createDefaultGridState(designId, modelGrid, core.windowId, 0);
				updateGridState(
					{
						...previous,
						distance: distance ?? previous.distance,
						units: units ?? previous.units,
						updatedAt: Date.now(),
						sourceWindowId: core.windowId,
					},
					true,
				);
			}

			if (Object.keys(appearance).length === 0) {
				return;
			}
			if (isGridAppearanceSaved) {
				setSavedGridAppearance(modelType, appearance);
			} else {
				setSessionGridAppearances((current) => ({
					...current,
					[modelType]: { ...(current[modelType] ?? defaultGridAppearance), ...appearance },
				}));
			}
		},
		[core.windowId, defaultGridAppearance, isGridAppearanceSaved, setSavedGridAppearance, updateGridState],
	);

	const noteViewportChanged = useCallback(() => {
		viewUpdatedAtRef.current = Date.now();
	}, []);

	useInteractionStateBridge({
		core,
		interactionManager,
		suppressHistoryForHydrationRef,
	});
	useLayerBridge({
		core,
		layerStates,
		updateLayerState,
	});
	useGridBridge({
		core,
		gridStates,
		updateGridState,
	});
	useViewHydrationBridge({
		core,
		activeViewRef,
		getActiveViewportRef,
		setActiveViewRef,
		restoreViewportRef,
		suppressHistoryForHydrationRef,
		viewUpdatedAtRef,
	});

	return useMemo(
		() => ({
			status: core.status,
			drawingManager: {
				registerDrawingManager,
			},
			layers: {
				layerStates,
				setLayerVisibility,
				setLayerColorOverride,
			},
			grid: {
				defaultGridAppearance,
				getGridSettings,
				setGridSettings,
			},
			view: {
				noteViewportChanged,
			},
		}),
		[
			core.status,
			defaultGridAppearance,
			getGridSettings,
			layerStates,
			noteViewportChanged,
			registerDrawingManager,
			setGridSettings,
			setLayerColorOverride,
			setLayerVisibility,
		],
	);
}
